import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import VidhyaWordmark from "./components/VidhyaWordmark";
import "./landing.css";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Legal · Privacy",
    file: "/legal/privacy.md",
    altPath: "/terms",
    altLabel: "Read Terms & Conditions →",
  },
  terms: {
    title: "Terms & Conditions",
    eyebrow: "Legal · Terms",
    file: "/legal/terms.md",
    altPath: "/privacy",
    altLabel: "Read Privacy Policy →",
  },
  extensionPrivacy: {
    title: "Vidhya Autofill — Extension Privacy Policy",
    eyebrow: "Legal · Browser extension",
    file: "/legal/extension-privacy.md",
    altPath: "/privacy",
    altLabel: "Read main Privacy Policy →",
  },
};

function LegalPage({ doc }) {
  const meta = DOCS[doc];
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(meta.file)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load document (${r.status})`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        setContent(text);
        setLoading(false);
        // Scroll to top whenever doc changes
        window.scrollTo({ top: 0, behavior: "instant" });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e.message || e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [meta.file]);

  return (
    <div className="legal-shell" data-testid={`legal-${doc}-page`}>
      <header className="legal-topbar">
        <button
          type="button"
          className="legal-brand"
          onClick={() => navigate("/")}
          aria-label="Vidhya home"
          data-testid="legal-home-link"
        >
          <VidhyaWordmark size={32} />
        </button>
        <nav className="legal-topnav">
          <Link
            to="/privacy"
            className={`legal-topnav-link ${doc === "privacy" ? "active" : ""}`}
            data-testid="legal-nav-privacy"
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            className={`legal-topnav-link ${doc === "terms" ? "active" : ""}`}
            data-testid="legal-nav-terms"
          >
            Terms
          </Link>
          <a
            href="mailto:hello@govidhya.com"
            className="legal-topnav-link"
            data-testid="legal-nav-contact"
          >
            Contact
          </a>
        </nav>
      </header>

      <main className="legal-main">
        <div className="legal-eyebrow">{meta.eyebrow}</div>
        <h1 className="legal-h1">{meta.title}</h1>

        {loading && <p className="legal-muted" data-testid="legal-loading">Loading…</p>}
        {error && (
          <p className="legal-error" data-testid="legal-error">
            Couldn't load the document. {error}
            <br />
            <a href="mailto:hello@govidhya.com">Email us for a copy.</a>
          </p>
        )}

        {!loading && !error && (
          <article className="legal-article" data-testid="legal-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </article>
        )}

        <div className="legal-footer-actions">
          <Link to={meta.altPath} className="legal-altlink" data-testid="legal-alt-link">
            {meta.altLabel}
          </Link>
          <Link to="/" className="legal-altlink" data-testid="legal-back-home">
            ← Back to Vidhya
          </Link>
        </div>

        <p className="legal-contact-block">
          Questions? Reach us at{" "}
          <a href="mailto:hello@govidhya.com">hello@govidhya.com</a>.
        </p>
      </main>

      <footer className="legal-bottom">
        © {new Date().getFullYear()} Super Apply LLC · Vidhya · govidhya.com
      </footer>
    </div>
  );
}

export const PrivacyPolicy = () => <LegalPage doc="privacy" />;
export const TermsAndConditions = () => <LegalPage doc="terms" />;
export const ExtensionPrivacy = () => <LegalPage doc="extensionPrivacy" />;
